import {
  ORDER_DISPATCH_REPORTS,
  type OrderDispatchReportKey,
  ORDER_DISPATCH_REPORT_PERMISSION,
  DISPATCH_KIND_META,
  DISPATCH_KINDS,
  DISPATCH_STOCK_TRANSFER_FORM,
  DRYING_SLIP_FORM,
  ARRIVAL_REPORT_LIST,
  QUALITY_REPORT_LIST,
  QUALITY_REPORT_PERMISSION,
  GOT_REPORT_PERMISSION,
  REPORT_PERMISSIONS,
  type GotWebReportKey,
  LOT_BLENDING_FORM,
  PACKAGING_REPORT_LIST,
  PACKAGING_REPORT_PERMISSION,
  PACKING_SLIP_FORM,
  PACKING_WORK_ORDER_FORM,
  PROCESSING_PACKING_SLIP_FORM,
  REPRINT_WORK_ORDER_FORM,
  ROOT_STOCK_NOS_COUNT_FORM,
  PACK_P2C_FORM,
  IVT_FORM,
  OFL_PACKING_FORM,
  PACK_REVALIDATE_FORM,
  UNPACKING_FORM,
  FC_PACKING_FORM,
  PROCESSING_SLIP_FORM,
  type GateLabels,
  type NavCounts,
  type Permission,
  stageReportCodes,
  SLOC_DESK_PERMISSION,
  STOCK_REPORT_LIST,
  STOCK_REPORT_PERMISSION,
  WAREHOUSE_STAGE_REPORTS,
  STORES_REPORT_KEYS,
  STORES_REPORTS,
  PRIMING_FORM,
  THERMAL_FORM,
  COLOR_SORT_FORM,
  TREATMENT_FORM,
  isStoresOnly,
} from "@nexseed/shared";
import {
  Hash,
  Archive,
  BadgeCheck,
  Cog,
  BarChart3,
  Merge,
  Package,
  Send,
  Sun,
  BookA,
  MessageSquareText,
  Boxes,
  RotateCcw,
  ScanSearch,
  CalendarClock,
  FileSpreadsheet,
  Wheat,
  ClipboardList,
  ListChecks,
  PauseCircle,
  Droplets,
  Microscope,
  FlaskConical,
  PackageX,
  LayoutDashboard,
  Plug,
  ScrollText,
  ShieldCheck,
  Truck,
  KeyRound,
  Users,
  type LucideIcon,
  Sprout,
  Wind,
  Warehouse,
  MapPin,
  Ruler,
  ShieldAlert,
  PackageMinus,
  Layers,
  ClipboardCheck,
  FileUp,
  FileBarChart,
  History,
  Lock,
  Scale,
  ScanEye,
  SprayCan,
  Unlock,
  Tag,
  Award,
  BookOpen,
  Dna,
  PackagePlus,
  Store,
  Flame,
  Trash2,
  ShoppingCart,
  Search,
  Handshake,
  PackageCheck,
  PackageOpen,
  ArrowLeftRight,
  Printer,
  FileWarning,
  Hourglass,
  UserCheck,
} from "lucide-react";
import {
  FORM_BY_SLUG,
  FORM_ICON,
  FORM_SLUGS,
  formTitle,
  type FormSlug,
} from "@/features/arrival/forms";
import { MASTERS, SPECIAL_SCREENS } from "@/features/masters/registry";
import { STAGES } from "@/features/warehouse/labels";
import { masterPath, type MasterKey } from "@/features/masters/spec";

export interface NavLeaf {
  kind: "link";
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: Permission | undefined;
  /** A second code that also opens the page: shown to a login holding either. */
  orPermission?: Permission | undefined;
  /** Codes of what the page holds (its reports): shown only to a login holding one of them. */
  anyPermission?: readonly Permission[] | undefined;
  /** Codes the page needs on top of `permission`: shown only to a login holding every one. */
  allPermissions?: readonly Permission[] | undefined;
  /** A form code: shown only when the login's roles grant viewing that form. */
  form?: string | undefined;
  /** Form codes: shown when the login's roles grant viewing any one of them. */
  anyForm?: readonly string[] | undefined;
  /** Reference page open to every login; never the "/" landing target. */
  reference?: boolean | undefined;
  exact?: boolean | undefined;
  search?: Record<string, string> | undefined;
  /** Named by the login's roles (`roles.gate_labels`): `label` is only the standard name. */
  gateModule?: boolean | undefined;
  /** One line under the label in the top menu's drop-down panel. */
  hint?: string | undefined;
  /** The drop-down panel's column heading this entry sits under (e.g. Quality: Sampling / Tests). */
  group?: string | undefined;
  /** An optional module the server may have switched off (`/me.features`): hidden while it is. */
  feature?: keyof NavFeatures | undefined;
  /**
   * Child pages drawn indented under this one (SLOC Updation's RSW / CSW / PSW). A login that may
   * open two or more sees this page with just those under it; one sees that child alone under its
   * `promotedLabel`, opening it directly; none sees neither (user, 2026-10-13).
   */
  subLinks?: NavLeaf[] | undefined;
  /** A sub-link's name when it stands alone, without its parent above it. */
  promotedLabel?: string | undefined;
  /** An index of its section's other pages: the section is dropped when nothing else is left. */
  hub?: boolean | undefined;
  /** Another menu's page listed here for reach: it never makes this menu the current one. */
  elsewhere?: boolean | undefined;
  /** Hidden from a login this returns true for, whatever codes it holds (e.g. QA staff's Dashboard). */
  hiddenWhen?: ((v: NavViewer) => boolean) | undefined;
}
/** Optional modules, as `/me.features` reports them. */
export interface NavFeatures {
  feedback?: boolean | undefined;
}
export interface NavGroup {
  kind: "group";
  label: string;
  icon: LucideIcon;
  permission?: Permission | undefined;
  /** Path prefix that keeps the group open. */
  match: string;
  children: NavLeaf[];
}
export interface NavSection {
  title: string;
  /** The top menu bar's icon for this section; a one-page section falls back to its page's icon. */
  icon?: LucideIcon | undefined;
  /** The heading follows the Gate module's role-wise name once a role renames it. */
  gateModule?: boolean | undefined;
  items: (NavLeaf | NavGroup)[];
}

const link = (l: Omit<NavLeaf, "kind">): NavLeaf => ({ kind: "link", ...l });

/*
 * Every page carries a one-line hint under its name in the drop-down panel (user, 2026-10-01: every
 * module, like the Dryer's). The generated entries take theirs from these maps; a Record makes a new
 * form or master without one a type error, and nav.test.ts checks the hand-written ones.
 */
const FORM_HINT: Record<FormSlug, string> = {
  pdn: "Production seed from growers, bag by bag",
  "s-capture": "Trucks booked from the S-Capture feed",
  trading: "Seed bought from traders",
  "stock-transfer": "Stock received from another plant",
  cob: "Cob trucks unloaded at the dryer",
  "cob-receipt": "Dried seed received back from the dryer",
};
/** An Orders and Dispatch report's Reports-menu entry, named as SeedTrac's menu names it. */
const orderDispatchReport = (
  k: OrderDispatchReportKey,
  group: "Order Booking" | "Dispatch"
) =>
  link({
    to: `/reports/orders-dispatch/${k}`,
    label: ORDER_DISPATCH_REPORTS[k].menu,
    hint: ORDER_DISPATCH_REPORTS[k].title,
    group,
    icon: group === "Order Booking" ? ClipboardList : FileBarChart,
    permission: ORDER_DISPATCH_REPORT_PERMISSION[k],
  });

const MASTER_HINT: Record<MasterKey, string> = {
  facilities: "Plants and dryers; the code prefixes lots",
  "crop-categories": "Vegetable, Field crop and the like",
  crops: "Crops under each category",
  classifications: "F1, OP, FS, RS",
  varieties: "A crop's varieties and their stages",
  "sp-decode-map": "SP male × female pair → variety",
  vendors: "Suppliers, organisers and traders",
  "year-codes": "Season letter in lot and document numbers",
  "loss-types": "Reasons for an arrival's difference",
  "processing-resources":
    "Machines, drying floors and machines, pack sizes, pack lines",
  operators: "Processing and packing operators by team",
  "treatment-schemas": "Treatment colours, codes and crop / variety mappings",
  "pack-layouts": "Pouches per window box, boxes per master pack",
  "mp-types": "Carton and bag master packs, with their tare",
  "production-personnel": "Who an arrival can be e-mailed to",
  "email-cc": "Always copied on Unloading Sheet e-mails",
  "stores-uoms": "Kg, Number, Litres… and their decimals",
  "stores-classifications": "Sticker, Roll, Pouch, Bags, Chemicals…",
  "stores-items": "What the store keeps, with its unit and re-order level",
  "stores-parties": "C&F agents and dealers",
  "qc-analysts": "The lab's QA team per plant: type and the tests each does",
  customers: "Branches, C&F, dealers and buyers orders go to",
};
/** The Stores masters: their own column in the Masters panel, "Stores Masters" (user, 2026-10-07). */
const isStoresMaster = (k: MasterKey) => k.startsWith("stores-");
const STORES_MASTERS_GROUP = "Stores Masters";
/** GOT Location and Farmer masters: their own column in the Masters panel (user, 2026-10-21). */
const GOT_MASTERS_GROUP = "GOT Masters";
/**
 * The plant's own masters, by what they describe (user, 2026-10-22: the dropdowns were "messy and
 * unorganized"), so the Masters panel reads as columns rather than one block of nineteen.
 */
const MASTER_GROUP: Record<string, string> = {
  "/masters/facilities": "Plant & storage",
  "/masters/locations": "Plant & storage",
  "/masters/storage-restrictions": "Plant & storage",
  "/masters/crop-categories": "Crops & varieties",
  "/masters/crops": "Crops & varieties",
  "/masters/classifications": "Crops & varieties",
  "/masters/varieties": "Crops & varieties",
  "/masters/sp-decode-map": "Crops & varieties",
  "/masters/year-codes": "System",
  "/masters/vendors": "Parties & people",
  "/masters/production-personnel": "Parties & people",
  "/masters/email-cc": "Parties & people",
  "/masters/qc-analysts": "Parties & people",
  "/masters/customers": "Parties & people",
  "/masters/processing-resources": "Processing & packing",
  "/masters/operators": "Processing & packing",
  "/masters/treatment-schemas": "Processing & packing",
  "/masters/pack-layouts": "Processing & packing",
  "/masters/mp-types": "Processing & packing",
  "/masters/loss-types": "Processing & packing",
  "/masters/mail-settings": "System",
  "/masters/api-feed": "System",
};
/** A master leaf with its column: its own group (Stores, GOT) or the plant masters' map. */
const inMasterGroup = (l: NavLeaf): NavLeaf =>
  l.group ? l : { ...l, group: MASTER_GROUP[l.to] ?? "Other masters" };
/** In seedtrac STORES' Masters menu order: Classification, Item, then units and parties. */
const STORES_MASTER_ORDER = [
  "stores-classifications",
  "stores-items",
  "stores-uoms",
  "stores-parties",
] as const satisfies readonly MasterKey[];
const SPECIAL_HINT: Record<string, string> = {
  "/masters/locations": "Warehouse → bin → sub-bin tree",
  "/masters/storage-restrictions": "Varieties that may not share a bin",
  "/masters/mail-settings": "Each plant's mail account for invites and e-mails",
  "/masters/api-feed": "Lots received from S-Capture, COB and Core PDN",
  "/masters/got-locations": "States and villages where GOT is grown",
  "/masters/got-farmers": "Farmers who grow GOT plots",
  "/masters/got-ppn-standards": "Standard PPN and days to observation per crop",
};

/**
 * One source of truth for navigation; routes carry the same permission in their beforeLoad.
 * Every entry resolves to a real page or a "coming in Phase N" page — never a 404.
 */
/** What each GOT report holds, from its legacy heading (features/got/reports/defs.ts). */
const GOT_REPORT_HINT: Record<GotWebReportKey, string> = {
  "sample-dispatch": "GOT samples dispatched in a period",
  pending: "Lots still waiting for a GOT result",
  "daily-result": "GOT results recorded on one day",
  result: "GOT results over a period",
  "periodical-result-qcs": "GOT results over a period, as QCS lists them",
  "periodical-result-qcm": "GOT results over a period, as QCM lists them",
  "sowing-pending": "GOT samples not yet sown",
  "transplantation-pending": "Sown lots not yet transplanted",
  "data-pending": "Plots whose GOT data is still to come",
  "prod-person-status":
    "GOT status by production person, over an arrival period",
  "final-grade": "Final GOT grade of each lot over a period",
  "data-in-terra": "GOT field data of the IN-TERRA replications",
  "data-in-situ": "GOT field data of the IN-SITU replications",
  "qcb-sowing-pending": "Sowing pending, summed by crop and variety",
  "qcb-transplant-pending": "Transplanting pending, summed by crop and variety",
  "qcb-observation-pending": "Observations pending, summed by crop and variety",
  "qcb-biotech": "Biotech test initiatives, summed",
  "qcb-periodical": "GOT results over a period, as QCB lists them",
  "qcb-data": "GOT field data, as QCB lists it",
};

/** The Inventory reports, the tabs of /inventory/reports, each on its own code (2026-10-05). */
const INVENTORY_REPORTS: {
  tab: string;
  label: string;
  hint: string;
  permission: Permission;
}[] = [
  {
    tab: "ageing",
    label: "Stock Ageing Report",
    hint: "How long each lot has sat at its plant, with days since its last QC test",
    permission: "inventory.reports.ageing",
  },
  {
    tab: "summary",
    label: "Stock Summary Report",
    hint: "Crop, variety and stage: passed, under testing, held and on Quarantine",
    permission: "inventory.reports.summary",
  },
  {
    tab: "validity",
    label: "QC Validity Report",
    hint: "Lots whose passed QC runs out within chosen days, or already has",
    permission: "inventory.reports.qc_validity",
  },
];

/**
 * Reports > Quality (user, 2026-10-28): the user's QC report list, in its order (#1–16, then #18
 * Periodical Arrival). Three already exist elsewhere and are linked from here: #3 Periodical GOT
 * Result (QCS), #4 QC Ageing and #13 Periodical Processing. The rest are /reports/qc/<slug>, each
 * on its own qc.reports.* tick alone (user, 2026-10-29: "show only upto thatmuch the permission is
 * granted").
 */
function qualityReportLeaves(): NavLeaf[] {
  const built = new Map(
    QUALITY_REPORT_LIST.map((r) => [
      r.slug,
      link({
        to: `/reports/qc/${r.slug}`,
        label: r.def.menu,
        hint: r.hint,
        group: "Quality",
        icon: ClipboardList,
        permission: QUALITY_REPORT_PERMISSION[r.slug],
      }),
    ])
  );
  const leaf = (slug: (typeof QUALITY_REPORT_LIST)[number]["slug"]) =>
    built.get(slug)!;
  return [
    leaf("sample-pending"),
    leaf("result-pending"),
    leaf("germ-first-count-list"),
    leaf("germ-evaluation-list"),
    link({
      to: "/quality/got/reports/periodical-result-qcs",
      label: "Periodical GOT Result Report",
      hint: GOT_REPORT_HINT["periodical-result-qcs"],
      group: "Quality",
      icon: FileBarChart,
      permission: GOT_REPORT_PERMISSION["periodical-result-qcs"],
    }),
    link({
      to: "/quality/qc-ageing",
      label: "QC Test Ageing Status Report",
      hint: "Lots by days since their last OK QC test",
      group: "Quality",
      icon: Hourglass,
      permission: "processing.reports.qc_ageing",
    }),
    leaf("substandard"),
    leaf("soft-release"),
    leaf("super-soft-release"),
    leaf("blending"),
    leaf("stock"),
    leaf("sample-collection"),
    leaf("stock-status"),
    leaf("density"),
    link({
      to: "/processing/reports/processing",
      label: "Periodical Processing Report",
      hint: "Raw against condition seed per lot, with RM, IM, IL and total loss",
      group: "Quality",
      icon: BarChart3,
      permission: "processing.reports.processing",
    }),
    leaf("testing-data"),
    leaf("pp-testing"),
    leaf("moisture-testing"),
    leaf("periodical-arrival"),
  ];
}

/** A Processing report's page, /processing/reports/$key. */
export type ProcessingReportKey =
  | "processing"
  | "drying"
  | "priming"
  | "color-sort"
  | "consolidated"
  | "organiser-status"
  | "personnel-status"
  | "got-ok"
  | "periodical-got";

/**
 * The Processing reports (SeedTrac p326–333), in menu order: each its own page under
 * /processing/reports, listed there like every module's reports (user, 2026-10-28: no tabs).
 */
export const PROCESSING_REPORTS: {
  key: ProcessingReportKey;
  label: string;
  hint: string;
  permission: Permission;
}[] = [
  {
    key: "processing",
    label: "Periodical Processing Report",
    hint: "Raw against condition seed per lot, with RM, IM, IL and total loss",
    permission: "processing.reports.processing",
  },
  {
    key: "drying",
    label: "Periodical Drying Report",
    hint: "Before and after drying, and the drying loss",
    permission: "processing.reports.drying",
  },
  // Priming (docs/designs/priming.md): the lots that came back primed.
  {
    key: "priming",
    label: "Priming Register",
    hint: "Primed lots by return date: issued, batches, returned, loss and germination after",
    permission: "processing.reports.priming",
  },
  // Color Sorting (docs/designs/color-sorting.md): the colour sorter register.
  {
    key: "color-sort",
    label: "Periodical Color Sorting",
    hint: "Lots through the colour sorter: good and rejected, machine, supervisor, PP and moisture",
    permission: "processing.reports.color_sort",
  },
  {
    key: "consolidated",
    label: "Consolidated Processing Report",
    hint: "Processing by crop and variety, with the latest slip's details",
    permission: "processing.reports.consolidated",
  },
  {
    key: "organiser-status",
    label: "Organiser wise Processing Status Report",
    hint: "Each arrival lot of an organiser and where its processing stands",
    permission: "processing.reports.organiser_status",
  },
  {
    key: "personnel-status",
    label: "Production Personnel wise Processing Status Report",
    hint: "Each arrival lot of a production person and where its processing stands",
    permission: "processing.reports.personnel_status",
  },
  {
    key: "got-ok",
    label: "GOT OK Report",
    hint: "GOT OK raw seed still awaiting processing, oldest first",
    permission: "processing.reports.got_ok",
  },
  {
    key: "periodical-got",
    label: "Periodical GOT Report",
    hint: "GOT results in a period, by result",
    permission: "processing.reports.periodical_got",
  },
];

export const NAV: NavSection[] = [
  {
    // First, so a login that may open it lands here from "/". Its own code, not document.view: a
    // login narrowed to a few forms works them without seeing plant-wide numbers, and lands on the
    // first entry below it may open instead.
    //
    // Rail order follows the seed's path: Dashboard, Gate, Arrivals, Quality, Inventory, Dispatch, then
    // Admin (masters and access). Masters sits last so a login without the dashboard lands on work.
    title: "Overview",
    icon: LayoutDashboard,
    items: [
      link({
        to: "/dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        permission: "dashboard.view",
        hiddenWhen: isQaFloor,
        hint: "Plant-wide figures for today",
      }),
      // One Dashboard for everyone (user, 2026-10-20; docs/designs/qa-dashboard.md): the QC
      // Dashboard became the QA sections on top of it, and /qc-dashboard redirects here.
    ],
  },
  {
    title: "Gate Entry",
    gateModule: true,
    items: [
      // The gate leads the section because it leads the process: an S-Capture arrival cannot be
      // booked until the truck behind it has been passed in.
      link({
        to: "/gate",
        label: "Gate",
        icon: ShieldCheck,
        permission: "gate.view",
        search: { tab: "inward_pending" },
        gateModule: true,
        hint: "Trucks in and out at the gate",
      }),
    ],
  },
  {
    // A heading with every form under it, always open, as the rail was first laid out. Masters is
    // the one group that folds: it is long and visited rarely; the forms are the daily work.
    title: "Arrivals",
    icon: Truck,
    items: FORM_SLUGS.flatMap((slug) => {
      const form = link({
        to: `/arrivals/${slug}`,
        label: formTitle(slug),
        icon: FORM_ICON[slug],
        permission: "document.view",
        form: FORM_BY_SLUG[slug],
        hint: FORM_HINT[slug],
      });
      // The Dryer follows Cob Arrival: the cob lots go there once the truck's arrival is submitted,
      // and are blended, dried and loaded from there whichever truck brought them (client,
      // 2026-09-29). Not under Processing, whose Drying Slip is the plant's own form.
      return slug === "cob"
        ? [
            form,
            link({
              to: "/dryer",
              label: "Dryer",
              icon: Wind,
              permission: "document.view",
              form: FORM_BY_SLUG[slug],
              hint: "Blend, dry and load cob lots from any truck",
            }),
          ]
        : [form];
    }).concat(
      // The PP readings QC has verified, the same rows as QC › PP Verification's Verified tab (user,
      // 2026-10-28): read-only, after the forms, for any login that works an arrival form.
      link({
        to: "/arrivals/pp-appearance",
        label: "PP Appearance",
        icon: FlaskConical,
        permission: "document.view",
        anyForm: Object.values(FORM_BY_SLUG),
        hint: "Physical purity results verified by QC",
      })
    ),
  },
  {
    // The QC lab's worklists. After Gate Entry and Arrivals because a sample exists only once an arrival or a
    // request has raised it.
    title: "Quality",
    icon: FlaskConical,
    items: [
      // The QC lab's day's work, in process order (user, 2026-10-16). GOT has its own tab, every QC
      // and GOT report is under Reports, and Look-up (a lot's history, the pass marks, the GOT
      // masters) is its own tab.
      link({
        to: "/quality/pending",
        label: "QC Sample Pending",
        hint: "Draw samples from trucks on the floor",
        group: "Sampling",
        icon: FlaskConical,
        permission: "qc.sample_pending",
      }),
      // QC Sample Allocation (user, 2026-10-14): the supervisor gives each sample's Moisture, PP and
      // Germination to an analyst, SRF by SRF, and follows every test's progress.
      link({
        to: "/quality/allocation",
        label: "Sample Allocation",
        hint: "Give each SRF's tests to the lab's analysts",
        group: "Sampling",
        icon: UserCheck,
        permission: "qc.allocate",
      }),
      // The RSW/CSW desk's QC Sampling Request (manual p126): ask for lots to be sampled.
      link({
        to: "/quality/requests",
        label: "QC Sampling Request",
        hint: "Ask QC to sample a stored lot",
        group: "Sampling",
        icon: ClipboardList,
        permission: "qc.request",
      }),
      // Where each sample's guard packet is kept once its result is updated, and when it may go
      // (user, 2026-10-13, under Sampling; seedtrac Sample Collection report, manual p193–194).
      link({
        to: "/quality/guard-samples",
        label: "Collected Samples & Guard Sample Storage",
        hint: "Where each guard sample is kept and when it goes",
        group: "Sampling",
        icon: Archive,
        permission: "qc.guard_samples",
      }),
      // QC's half of the GOT hand-over (user, 2026-10-21: QC sends, so it sits with QC's work): the
      // collected test-T samples go to the GOT team on a GHN note, received on GOT ▸ Samples from QC.
      link({
        to: "/quality/got/handover",
        label: "GOT Hand-over",
        hint: "QC sends GOT samples to the GOT team on a GHN note",
        group: "Sampling",
        icon: Handshake,
        permission: "got.handover_send",
      }),
      // The QC supervisor's moisture check (QCS, manual p170): readings the analyst submitted on the
      // handheld, waiting to be verified.
      link({
        to: "/quality/moisture",
        label: "Moisture Verification",
        hint: "Record and verify moisture %",
        group: "Results",
        icon: Droplets,
        permission: "qc.verify",
      }),
      // Germination: the Lab Manager sets trays up (To set up tab, user 2026-10-22); counts come
      // from the handheld; the supervisor picks SGT, FGT or RE and the QC Result.
      link({
        to: "/quality/germination",
        label: "Germination Verification",
        hint: "Setup, counts, photos and verified %",
        group: "Results",
        icon: Sprout,
        permission: "qc.verify",
        orPermission: "qc.germination_setup",
      }),
      // Physical Purity readings submitted from the handheld, waiting for the supervisor's Result.
      link({
        to: "/quality/pp",
        label: "PP Verification",
        hint: "Physical purity results",
        group: "Results",
        icon: Microscope,
        permission: "qc.verify",
      }),
      // Density data (user, 2026-10-16; QCS p174–176, QCM p221): Raw / Condition density weights,
      // the review of processed Condition lots, and the QC Manager's edit.
      link({
        to: "/quality/density",
        label: "Density Data",
        hint: "Raw and Condition density, condition loss review",
        group: "Results",
        icon: Scale,
        permission: "qc.density",
        orPermission: "qc.density_edit",
      }),
      // SRF for RT Lots (user, 2026-10-16; QCS "RT SRF Update", p177–178): Retest samples batched
      // onto an SRF-RT for the lab.
      link({
        to: "/quality/rt-srf",
        label: "SRF for RT Lots",
        hint: "Batch Retest lots onto an RT SRF for the lab",
        group: "Sampling",
        icon: FlaskConical,
        permission: "qc.allocate",
      }),
      // Damaged/Consumed samples (QC fail rules, Phase 2): the supervisor approves a fresh draw from the lot,
      // or rejects the report.
      link({
        to: "/quality/damaged-samples",
        label: "Damaged/Consumed Samples",
        hint: "Approve a new sample for a damaged or consumed packet",
        group: "Sampling",
        icon: PackageX,
        permission: "qc.verify",
      }),
      // The QC Supervisor's side of Lot Blending (QCS, manual p153): authorise or reject what
      // Processing requested, then blend it into its 9-series lot.
      link({
        to: "/quality/blending",
        label: "Lot Blending Authorization",
        hint: "Authorise or reject blend requests",
        group: "Release & blending",
        icon: BadgeCheck,
        permission: "qc.verify",
      }),
      // An Under Testing lot moves only on one of these (user, 2026-09-30; QCS p155, QCM p217). The
      // dot counts lots that then failed QC and nobody has acknowledged.
      link({
        to: "/quality/releases/sr",
        label: "Soft Release",
        hint: "Move Under Testing GOT-NR (not recommended) lots",
        group: "Release & blending",
        icon: Unlock,
        permission: "qc.soft_release",
        orPermission: "qc.super_soft_release",
      }),
      link({
        to: "/quality/releases/ssr",
        label: "Super Soft Release",
        hint: "Move Under Testing GOT-R (recommended) lots",
        group: "Release & blending",
        icon: ShieldAlert,
        permission: "qc.super_soft_release",
        orPermission: "qc.soft_release",
      }),
    ],
  },
  {
    // The Grow-Out Test in its own tab (user, 2026-10-16: "separate the GOT related modules in a new
    // tab"), out of Quality. The pages keep their /quality/got paths; the top bar lights GOT for
    // them because its paths are the longer match.
    title: "GOT",
    icon: Dna,
    items: [
      // Rebuilt from seedtrac's legacy web GOT screens under their legacy menu labels (docs/designs/got-legacy-map.md), one got.* code per legacy screen.
      // QC → GOT hand-over (docs/designs/got-qc-handover.md): GOT receives here; QC sends from
      // Quality ▸ GOT Hand-over.
      link({
        to: "/quality/got/handover/receive",
        label: "Samples from QC",
        hint: "GOT team receives what QC sent, by personnel and SP pair",
        group: "Transactions",
        icon: PackageCheck,
        permission: "got.handover_receive",
      }),
      link({
        to: "/quality/got/dispatch",
        label: "GOT Sample Dispatch",
        hint: "Dispatch samples: GSDN, gate pass, GRS",
        group: "Transactions",
        icon: Send,
        permission: "got.dispatch",
      }),
      link({
        to: "/quality/got/print-sample",
        label: "Print GOT Sample",
        hint: "QC Sample Slips for GOT lots",
        group: "Transactions",
        icon: Tag,
        permission: "got.print_sample",
      }),
      link({
        to: "/quality/got/result-update",
        label: "GOT Result Update",
        hint: "IN-SITU / IN-TERRA replications and their data",
        group: "Transactions",
        icon: Dna,
        permission: "got.result_update",
      }),
      link({
        to: "/quality/got/low-ppn",
        label: "Low PPN",
        hint: "Counted below the Standard PPN: keep the sample, or resow it",
        group: "Transactions",
        icon: Sprout,
        permission: "got.resow_decide",
      }),
      link({
        to: "/quality/got/review",
        label: "GOT Data & Result Update",
        hint: "Review the field data and set the GOT Status",
        group: "Transactions",
        icon: ClipboardCheck,
        permission: "got.review",
      }),
      link({
        to: "/quality/got/final-grade",
        label: "Final GOT Grade Update",
        hint: "BTS, Prod. and Final GOT Grade",
        group: "Transactions",
        icon: Award,
        permission: "got.final_grade",
      }),
    ],
  },
  {
    // Look-up in its own tab (user, 2026-10-16: "take out look up too"), by module (user,
    // 2026-10-21): each look-up on its own code, so a login sees only its module's. The GOT masters
    // moved to Masters.
    title: "Look-up",
    icon: Search,
    items: [
      // A lot's whole story, once under Common for the Arrival, QC and GOT roles (user, 2026-10-21),
      // on qc.lot_biography alone: inventory desks open it from Inventory ▸ Lot Biography.
      link({
        to: "/warehouse/lot-biography",
        label: "Lot Biography",
        group: "Common",
        hint: "A lot's stock, movements and history",
        icon: History,
        permission: "qc.lot_biography",
      }),
      // Each crop's pass marks, for the QA Supervisor (user, 2026-10-21).
      link({
        to: "/quality/standards",
        label: "QC Standards",
        group: "Quality",
        hint: "What each crop needs to pass",
        icon: Ruler,
        permission: "qc.standards",
      }),
      // The packaging utilities, for Quality (user, 2026-10-28: "move all packaging utilities to
      // lookup for quality").
      link({
        to: "/quality/dov-calculator",
        label: "DoV Calculator",
        group: "Quality",
        hint: "Date of Validity from a DoT or DoSF",
        icon: CalendarClock,
        permission: "qc.dov_calculator",
      }),
      link({
        to: "/quality/barcode-search",
        label: "Barcode Search",
        group: "Quality",
        hint: "Which lot a pack sticker or OFL carton is on",
        icon: ScanSearch,
        permission: "qc.barcode_search",
      }),
      link({
        to: "/quality/batch-finder",
        label: "Batch No. Finder",
        group: "Quality",
        hint: "Every batch of a lot, with its stock",
        icon: Layers,
        permission: "qc.batch_finder",
      }),
      link({
        to: "/quality/got/biography",
        label: "GOT Biography",
        group: "GOT",
        hint: "A lot's GOT history, crop-variety or lot wise",
        icon: BookOpen,
        permission: "got.biography",
      }),
    ],
  },
  {
    // Posted stock, by where it is: storage (at rest) and floating (in transit).
    title: "Inventory",
    items: [
      link({
        to: "/inventory",
        label: "Inventory",
        hint: "Stock in bins and in transit",
        icon: Boxes,
        permission: "inventory.view",
      }),
      link({
        to: "/inventory/lots",
        label: "Lot Biography",
        hint: "A lot's stock, movements and history",
        icon: History,
        permission: "inventory.view",
      }),
      link({
        to: "/inventory/reservations",
        label: "Lot Reservations",
        hint: "Whole lots held for an export order",
        icon: Lock,
        permission: "inventory.view",
      }),
      link({
        to: "/inventory/opening",
        label: "Opening Balance",
        hint: "Load a plant's starting stock",
        icon: FileUp,
        permission: "inventory.opening",
      }),
      link({
        to: "/inventory/reconciliation",
        label: "Stock Reconciliation",
        hint: "Balances that drift from the ledger",
        icon: Scale,
        permission: "inventory.reconcile",
      }),
    ],
  },
  {
    // seedtrac's RSW desk (manual p291-315) for lots already in a bin, after an arrival's Final
    // Submit. The desk is stage-aware: CSW and PSW are the same screens for the C and P stages
    // and open behind the page's Raw | Condition | Pack switch (docs/designs/rsw-warehouse-module.md).
    title: "Warehouse",
    icon: Warehouse,
    items: [
      // One toggle per desk is SLOC Updation's whole access (user, 2026-10-13): "RSW SLOC Updation"
      // (warehouse.sloc.raw) and its CSW / PSW siblings. The parent opens the desk chooser and is
      // drawn with its desks under it when two or more are held; one desk stands alone as
      // "SLOC Updation · RSW" and opens that desk directly.
      link({
        to: "/warehouse/sloc",
        label: "SLOC Updation",
        hint: "RSW / CSW / PSW — Sub-bin wise or Crop Variety wise",
        group: "Transactions",
        icon: MapPin,
        anyPermission: Object.values(SLOC_DESK_PERMISSION),
        subLinks: STAGES.map((d) =>
          link({
            to: "/warehouse/sloc",
            search: { stage: d.stage },
            label: `${d.desk} · ${d.title}`,
            promotedLabel: `SLOC Updation · ${d.desk}`,
            hint: `${d.title} — Sub-bin wise or Crop Variety wise`,
            group: "Transactions",
            icon: MapPin,
            permission: SLOC_DESK_PERMISSION[d.stage],
          })
        ),
      }),
      link({
        to: "/warehouse/raw/seed-status",
        label: "Seed Status Updation",
        hint: "Drying, Fumigation, Quarantine",
        group: "Transactions",
        icon: ShieldAlert,
        // Its own toggle, "Seed Status Updation": View + the RSW desk alone no longer open it, or a
        // role whose admin turned the toggle off still landed here first (user, 2026-10-14).
        permission: "warehouse.seed_status",
        allPermissions: ["warehouse.view", "warehouse.desk.raw"],
      }),
      // The same request the Quality menu lists; here it opens straight on the new-request form.
      link({
        to: "/quality/requests/new",
        label: "QC Sampling Request",
        hint: "Ask QC to re-test a stored lot",
        group: "Transactions",
        icon: ClipboardList,
        permission: "qc.request",
        // Also needs Warehouse's own View: a QC-only role must not get a Warehouse menu from this.
        anyPermission: ["warehouse.view"],
      }),
      link({
        to: "/warehouse/raw/release",
        label: "Raw Seed Release",
        hint: "Release lots from the bin, print the Release Note",
        group: "Transactions",
        icon: PackageMinus,
        // Its own codes, as the API checks them: the RSW desk alone does not open it (user, 2026-10-13).
        permission: "warehouse.release",
        orPermission: "warehouse.release.cancel",
        anyPermission: ["warehouse.desk.raw"],
      }),
      // SeedTrac CSW's "Batch Creation" (Lot Number Batch Generation, 2026-10-22): its own toggle,
      // as the API checks it, plus the CSW desk.
      link({
        to: "/warehouse/condition/batch",
        label: "Batch Creation · CSW",
        hint: "Cut a Condition lot's next batch into a SLOC",
        group: "Transactions",
        icon: Layers,
        permission: "warehouse.batch",
        allPermissions: ["warehouse.view", "warehouse.desk.condition"],
      }),
      // The RSW reports moved to the Reports menu (user, 2026-10-15), and Lot Biography to Look-up
      // (2026-10-16).
    ],
  },
  {
    // seedtrac STORES (packages/shared/src/stores.ts): the consumables store, its menus as columns.
    // No Stores home (user, 2026-10-08); a screen that covers several types asks which in a pop-up.
    // Its masters are not here: they sit in Masters under "Stores Masters" (user, 2026-10-07).
    title: "Stores",
    icon: Store,
    items: [
      link({
        to: "/stores/arrivals",
        label: "Arrival",
        hint: "From Vendor, Stock Transfer, Internal Return",
        group: "Transactions",
        icon: PackagePlus,
        permission: "stores.view",
      }),
      link({
        to: "/stores/issues",
        label: "Issue",
        hint: "Internal on e-Indent / Physical Indent, Stock Transfer, Return to Vendor",
        group: "Transactions",
        icon: PackageMinus,
        permission: "stores.view",
      }),
      link({
        to: "/stores/captive",
        label: "Captive Consumption",
        hint: "Items used up for a party (CCN)",
        group: "Transactions",
        icon: Flame,
        permission: "stores.view",
      }),
      link({
        to: "/stores/discard",
        label: "Material Discard",
        hint: "Damaged stock written off (MDN)",
        group: "Transactions",
        icon: Trash2,
        permission: "stores.view",
      }),
      link({
        to: "/stores/sloc",
        label: "SLOC Updation",
        hint: "Move an item's stock between bins",
        group: "Transactions",
        icon: MapPin,
        permission: "stores.view",
      }),
      link({
        to: "/stores/counts",
        label: "Cycle Inventory",
        hint: "Count blind, approve the differences",
        group: "Transactions",
        icon: ClipboardCheck,
        permission: "stores.view",
      }),
      // The Reorder Level report, with Order Placement on each row (user, 2026-10-30).
      link({
        to: "/stores/reports/reorder-level",
        label: "Order Placement at Reorder",
        hint: "Items at their re-order level; record an order placed",
        group: "Transactions",
        icon: ShoppingCart,
        permission: "stores.view",
      }),
      // seedtrac's admin-only Transactions (stores-corrections.ts), one screen with a pill per kind
      // as Issue has: Good to Damage, Damage to Good, Excess/Shortage, Inter Item Transfer.
      link({
        to: "/stores/corrections",
        label: "Stock Corrections",
        hint: "Good to Damage, Damage to Good, Excess / Shortage, Inter Item Transfer",
        group: "Corrections",
        icon: Scale,
        permission: "stores.view",
      }),
      link({
        to: "/stores/opening",
        label: "Opening Stock",
        hint: "Load what the store holds from a sheet",
        group: "Corrections",
        icon: FileUp,
        permission: "stores.opening",
      }),
      // Its reports, and the old Utility (Sub-Bin Card, SLOC Search, Item Search), are in the Reports
      // menu, each by name (user, 2026-10-17 and 2026-10-30).
      // No separate e-Indents entry for the store (user, 2026-10-30: "minimum forms"): what the floor
      // asks for waits on Issue ▸ Internal – e-Indent's Pending e-Indents tab, and its leftovers on
      // Arrival ▸ Internal Return's Pending leftovers, each done there in one step. Packaging raises
      // its own from Packaging ▸ Store e-Indents.
    ],
  },
  {
    // seedtrac's Processing login (manual p316-325): the four transactions, in its menu's order.
    title: "Processing",
    icon: Cog,
    items: [
      link({
        to: "/processing/drying",
        label: "Drying Slip",
        hint: "Lots sent through the dryer",
        icon: Sun,
        permission: "document.view",
        form: DRYING_SLIP_FORM,
      }),
      link({
        to: "/processing/thermal",
        label: "Thermal Slip",
        hint: "Issue lots to the Thermal team; put away what comes back",
        icon: Flame,
        permission: "document.view",
        form: THERMAL_FORM,
        // Processing's side of the hand-off (user, 2026-10-29).
        anyPermission: ["processing.thermal.issue", "processing.thermal.sloc"],
      }),
      // The Thermal team's side: what Processing issued to it.
      link({
        to: "/thermal/pending",
        label: "Thermal Pending",
        hint: "Receive issued lots, fill the Thermal Slip, send them back",
        icon: Flame,
        permission: "document.view",
        form: THERMAL_FORM,
        anyPermission: [
          "processing.thermal.receive",
          "processing.thermal.slip",
        ],
      }),
      // Color Sorting (docs/designs/color-sorting.md): whole lots through the colour sorter.
      link({
        to: "/processing/color-sort",
        label: "Color Sorting Slip",
        hint: "Black and odd seed out on the colour sorter",
        icon: ScanEye,
        permission: "document.view",
        form: COLOR_SORT_FORM,
      }),
      // Treatment Slip (QC review, 2026-11-03): Condition lots treated after processing.
      link({
        to: "/processing/treatment",
        label: "Treatment Slip",
        hint: "Condition lots treated after processing; vegetables go back to QC",
        icon: SprayCan,
        permission: "document.view",
        form: TREATMENT_FORM,
      }),
      // Priming (docs/designs/priming.md): lots primed in batches, worked like Thermal.
      link({
        to: "/processing/priming",
        label: "Priming",
        hint: "Issue lots to R&D for priming; put away what comes back",
        icon: Droplets,
        permission: "document.view",
        form: PRIMING_FORM,
        anyPermission: ["processing.priming.issue", "processing.priming.sloc"],
      }),
      // R&D's side: what Processing issued to it for priming.
      link({
        to: "/rnd/priming",
        label: "Priming Pending (R&D)",
        hint: "Receive issued lots, fill the Priming Slip, send them back",
        icon: Droplets,
        permission: "document.view",
        form: PRIMING_FORM,
        anyPermission: [
          "processing.priming.receive",
          "processing.priming.slip",
        ],
      }),
      link({
        to: "/processing/slip",
        label: "Processing Slip",
        hint: "Grade raw seed into processed lots",
        icon: Cog,
        permission: "document.view",
        form: PROCESSING_SLIP_FORM,
      }),
      link({
        to: "/processing/packing-slip",
        label: "Processing & Packing Slip",
        hint: "Process and pack in one step",
        icon: Package,
        permission: "document.view",
        form: PROCESSING_PACKING_SLIP_FORM,
      }),
      link({
        to: "/processing/blending",
        label: "Lot Blending",
        hint: "Request lots to be blended into one",
        icon: Merge,
        permission: "document.view",
        form: LOT_BLENDING_FORM,
      }),
    ],
  },
  {
    // Condition seed into pouches, window boxes and master packs (docs/designs/packaging-module.md).
    title: "Packaging",
    icon: Package,
    items: [
      link({
        to: "/packaging/wb-packing-slip",
        label: "WB Packing Slip",
        hint: "Packing work orders: lots, line and priority for the floor",
        icon: Package,
        permission: "document.view",
        form: PACKING_WORK_ORDER_FORM,
      }),
      link({
        to: "/packaging/root-stock-count",
        label: "Root Stock Nos Count",
        hint: "Count packed root stock with no seed count, so it can move",
        icon: Hash,
        permission: "document.view",
        form: ROOT_STOCK_NOS_COUNT_FORM,
      }),
      link({
        to: "/packaging/packing-slip",
        label: "Packing Slip",
        hint: "Pack a released work order into pouches: labels, MP and PSW bins",
        icon: PackageCheck,
        permission: "document.view",
        form: PACKING_SLIP_FORM,
      }),
      link({
        to: "/packaging/reprint-slip",
        label: "Re-Printing Packing Slip",
        hint: "Packed lots back to the floor for new labels",
        icon: Printer,
        permission: "document.view",
        form: REPRINT_WORK_ORDER_FORM,
      }),
      link({
        to: "/packaging/p2c",
        label: "Pack Seed Unpacking (P2C)",
        hint: "Packed pouches back to condition seed, next batch",
        icon: PackageOpen,
        permission: "document.view",
        form: PACK_P2C_FORM,
      }),
      link({
        to: "/packaging/ivt",
        label: "Inter Variety Transfer",
        hint: "Condition lots to another variety, next batch (IVTN)",
        icon: ArrowLeftRight,
        permission: "document.view",
        form: IVT_FORM,
      }),
      link({
        to: "/packaging/ofl",
        label: "OFL Packing Slip",
        hint: "Field-crop lots packed whole into weighed cartons",
        icon: Boxes,
        permission: "document.view",
        form: OFL_PACKING_FORM,
      }),
      link({
        to: "/packaging/fc-packing-slip",
        label: "Packing Slip (Field Crop)",
        hint: "Field-crop lots packed into kg pouches, loss and CC off",
        icon: Wheat,
        permission: "document.view",
        form: FC_PACKING_FORM,
      }),
      // Legacy Packaging lists Processing's combined slips too, read only (user, 2026-10-29).
      link({
        to: "/packaging/pnp-slip",
        label: "Processing & Packing Slip",
        hint: "Slips Processing cleaned and packed in one pass, read only",
        icon: Package,
        permission: "document.view",
        form: PROCESSING_PACKING_SLIP_FORM,
      }),
      link({
        to: "/packaging/pack-reprint",
        label: "Pack Seed Re-Printing",
        hint: "Re-tested packed lots re-labelled with a new validity",
        icon: RotateCcw,
        permission: "document.view",
        form: PACK_REVALIDATE_FORM,
      }),
      link({
        to: "/packaging/unpackaging",
        label: "Unpackaging",
        hint: "Open master packs back into pouches or window boxes",
        icon: PackageOpen,
        permission: "document.view",
        form: UNPACKING_FORM,
      }),
      link({
        to: "/packaging/sathi",
        label: "SATHI Export",
        hint: "Packed lots in the SATHI portal's columns, as Excel",
        icon: FileSpreadsheet,
        permission: "document.view",
        form: PACKING_SLIP_FORM,
      }),
      // Ask the store for rolls, pouches and labels; send back what a whole roll leaves over
      // (user, 2026-10-30).
      link({
        to: "/packaging/store-indents",
        label: "Store e-Indents",
        hint: "Ask the store for items; return the leftover",
        icon: ClipboardList,
        permission: "stores.indent.raise",
      }),
    ],
  },
  {
    // Stock leaving the plant: the last step of the seed's path, so the last work section.
    title: "Dispatch",
    icon: Send,
    items: [
      // Orders and Dispatch (docs/designs/dispatch-module.md, user 2026-10-30): SeedTrac's Order
      // Booking login, then its Dispatch login, in one section.
      link({
        to: "/orders",
        label: "Orders",
        hint: "Book and approve Sales, Stock Transfer and TDF orders",
        group: "Orders",
        icon: ClipboardList,
        permission: "orders.view",
      }),
      link({
        to: "/orders/status",
        label: "Order Status",
        hint: "Ordered, dispatched and balance per item",
        group: "Orders",
        icon: ListChecks,
        permission: "orders.view",
      }),
      link({
        to: "/orders/holds",
        label: "Holding / Unholding",
        hint: "Hold a variety, party or order",
        group: "Orders",
        icon: PauseCircle,
        permission: "orders.view",
      }),
      ...DISPATCH_KINDS.map((k) =>
        link({
          to: `/dispatch/sales/${k}`,
          label: DISPATCH_KIND_META[k].title,
          hint: DISPATCH_KIND_META[k].hint,
          group: "Dispatch",
          icon: k === "pack" ? PackageCheck : k === "discard" ? Trash2 : Send,
          permission: "document.view",
          form: DISPATCH_KIND_META[k].form,
        })
      ),
      link({
        to: "/dispatch/barcode-lookup",
        label: "Barcode Lookup",
        hint: "Which dispatch, party and order a master pack went on",
        group: "Utility",
        icon: ScanSearch,
        permission: "document.view",
        form: DISPATCH_KIND_META.pack.form,
      }),
      link({
        to: "/dispatch/stock-transfer",
        label: "Stock Transfer Out",
        hint: "Send stock to another plant (raw, condition or pack seed)",
        group: "Dispatch",
        icon: Truck,
        permission: "document.view",
        form: DISPATCH_STOCK_TRANSFER_FORM,
      }),
    ],
  },
  {
    // Every report in one menu (user, 2026-10-15), and only here (2026-10-22: "put them under the
    // reports section only"). Each module lists its reports by name, in the menu bar's order, so the
    // panel reads as one directory (user, 2026-10-22: "messy and unorganized and inconsistent").
    // Each report has its own code under Roles → Reports, and that code alone shows it here (user,
    // 2026-10-16): no module view, desk or form grant.
    title: "Reports",
    icon: FileBarChart,
    items: [
      // The Reports page: drawn in the panel's head, not as a report of its own.
      link({
        to: "/reports",
        label: "All Reports",
        hint: "Every report you can open, by module",
        icon: FileBarChart,
        exact: true,
        hub: true,
      }),
      ...ARRIVAL_REPORT_LIST.map((r) =>
        link({
          to: `/reports/arrival/${r.slug}`,
          label: r.menu,
          hint: r.hint,
          group: "Arrival",
          icon: Truck,
          permission: "reports.arrival",
        })
      ),
      link({
        to: "/reports/variety-decoding",
        label: "Variety Decoding Report",
        hint: "SP pair (F × M) to variety, from the VNR SP API and Masters, and the pairs still coded",
        group: "Arrival",
        icon: KeyRound,
        permission: "reports.variety_decoding",
      }),
      link({
        to: "/reports/high-moisture",
        label: "High Moisture Lots",
        hint: "Arrival lots QC read above the crop's Normal moisture, until they are dried",
        group: "Arrival",
        icon: Droplets,
        permission: "reports.high_moisture",
      }),
      // Reports > Quality (user, 2026-10-28): exactly the user's QC report list, in its order.
      ...qualityReportLeaves(),
      // Order Booking and Dispatch reports (user, 2026-10-30): two groups, each SeedTrac's own report
      // menu in its order and words, holding only the reports NexSeed has.
      link({
        to: "/orders/status",
        label: "Booked Order Status Report",
        hint: "Every approved order item: ordered, dispatched and balance",
        group: "Order Booking",
        icon: ClipboardList,
        permission: "orders.view",
      }),
      orderDispatchReport("orders-pending", "Order Booking"),
      link({
        to: "/orders/holds",
        label: "Item on Hold Report",
        hint: "Varieties, parties and orders on hold",
        group: "Order Booking",
        icon: PauseCircle,
        permission: "orders.view",
      }),
      orderDispatchReport("orders-cancelled", "Order Booking"),
      orderDispatchReport("orders-suspended", "Order Booking"),
      orderDispatchReport("orders-booked", "Order Booking"),
      orderDispatchReport("pack-activity", "Dispatch"),
      orderDispatchReport("pack-ageing", "Dispatch"),
      // Periodical Dispatch (user, 2026-10-28): also on the hub's Key reports strip.
      link({
        to: "/reports/dispatch/periodical",
        label: "Periodical Dispatch Report",
        hint: "Every lot sent out in a period, with the truck, gate pass, QC and GOT",
        group: "Dispatch",
        icon: Truck,
        permission: "qc.reports.periodical_dispatch",
      }),
      orderDispatchReport("dispatch-dc", "Dispatch"),
      orderDispatchReport("dispatch-crop-variety", "Dispatch"),
      orderDispatchReport("dispatch-cnf-barcodes", "Dispatch"),
      // The legacy GOT reports by name, as Roles → Reports names them (QCS / QCM / QCB variants).
      ...(Object.keys(GOT_REPORT_PERMISSION) as GotWebReportKey[]).map((k) =>
        link({
          to: `/quality/got/reports/${k}`,
          label: REPORT_PERMISSIONS[GOT_REPORT_PERMISSION[k]].label,
          hint: GOT_REPORT_HINT[k],
          group: "GOT",
          icon: FileBarChart,
          permission: GOT_REPORT_PERMISSION[k],
        })
      ),
      link({
        to: "/quality/got/handover/report",
        label: "Dispatched without Hand-over Report",
        hint: "Daily: GOT samples dispatched before QC handed them over",
        group: "GOT",
        icon: FileWarning,
        permission: "got.handover_send",
      }),
      ...INVENTORY_REPORTS.map((r) =>
        link({
          to: "/inventory/reports",
          search: { tab: r.tab },
          label: r.label,
          hint: r.hint,
          group: "Inventory",
          icon: FileBarChart,
          permission: r.permission,
        })
      ),
      // The plants' stock pivots (user, 2026-10-23, "NeXseed Create New Report.xlsx").
      ...STOCK_REPORT_LIST.map((r) =>
        link({
          to: `/reports/stock/${r.slug}`,
          label: r.menu,
          hint: r.hint,
          group: "Inventory",
          icon: FileBarChart,
          permission: STOCK_REPORT_PERMISSION[r.key],
        })
      ),
      // RSW and CSW reports as one list (user, 2026-10-16): a report shows when the login holds it
      // for any stage, and its page switches between the stages it may open.
      ...WAREHOUSE_STAGE_REPORTS.map((r) =>
        link({
          to: `/reports/warehouse/${r.slug}`,
          label: r.menu,
          hint: r.hint,
          group: "Warehouse",
          icon: FileBarChart,
          anyPermission: stageReportCodes(r),
        })
      ),
      // Each Stores report by name (user, 2026-10-17: "directly show all the stores reports").
      ...STORES_REPORT_KEYS.map((k) =>
        link({
          to: `/stores/reports/${k}`,
          label: STORES_REPORTS[k].menu,
          hint: STORES_REPORTS[k].hint.replace(/\.$/, ""),
          group: "Stores",
          icon: FileBarChart,
          permission: STORES_REPORTS[k].permission,
          orPermission: STORES_REPORTS[k].openTo,
        })
      ),
      // The old Utility's Sub-Bin Card: every receipt and issue through one bin, with its card.
      link({
        to: "/stores/reports/bin-card",
        label: "Sub-Bin Card",
        hint: "Every receipt and issue through one bin, item by item; prints the bin card",
        group: "Stores",
        icon: FileBarChart,
        permission: "stores.view",
      }),
      ...PROCESSING_REPORTS.map((r) =>
        link({
          to: `/processing/reports/${r.key}`,
          label: r.label,
          hint: r.hint,
          group: "Processing",
          icon: BarChart3,
          permission: r.permission,
        })
      ),
      // Each Packaging report by name, as the legacy packging/ Reports menu lists them (2026-10-22).
      ...PACKAGING_REPORT_LIST.map((r) =>
        link({
          to: `/reports/packaging/${r.slug}`,
          label: r.menu,
          hint: r.hint,
          group: "Packaging",
          icon: FileBarChart,
          permission: PACKAGING_REPORT_PERMISSION[r.key],
        })
      ),
    ],
  },
  {
    // Everything that configures the plant rather than runs it, at the bottom so the daily work leads.
    // Not gated on a role called admin: each entry carries its own permission code.
    title: "Admin",
    icon: Users,
    items: [
      {
        kind: "group",
        label: "Masters",
        icon: ClipboardList,
        // No group-level gate: each master carries its own code now, so the group is shown when
        // any child is. A single `masters.view` here would be a code nothing enforces.
        match: "/masters",
        // The masters themselves, not their groups. Generated from the registry so the rail cannot
        // drift from it; Processing Plant leads because warehouses hang off a plant, and the two
        // special screens are named here because they are routes rather than registry keys.
        children: [
          link({
            to: masterPath("facilities"),
            label: MASTERS.facilities.title,
            hint: MASTER_HINT.facilities,
            icon: MASTERS.facilities.icon,
            permission: MASTERS.facilities.permission.view,
          }),
          ...SPECIAL_SCREENS.filter((x) => x.to === "/masters/locations").map(
            (x) =>
              link({
                to: x.to,
                label: x.title,
                hint: SPECIAL_HINT[x.to],
                icon: x.icon,
                permission: x.permission,
              })
          ),
          ...(Object.keys(MASTERS) as MasterKey[])
            .filter((k) => k !== "facilities" && !isStoresMaster(k))
            .map((k) =>
              link({
                to: masterPath(k),
                label: MASTERS[k].title,
                hint: MASTER_HINT[k],
                icon: MASTERS[k].icon,
                permission: MASTERS[k].permission.view,
              })
            ),
          // GOT's masters have their own column below, on got.locations.
          ...SPECIAL_SCREENS.filter(
            (x) =>
              x.to !== "/masters/locations" && !x.permission.startsWith("got.")
          ).map((x) =>
            link({
              to: x.to,
              label: x.title,
              hint: SPECIAL_HINT[x.to],
              icon: x.icon,
              permission: x.permission,
            })
          ),
          ...STORES_MASTER_ORDER.map((k) =>
            link({
              to: masterPath(k),
              label: MASTERS[k].title,
              hint: MASTER_HINT[k],
              group: STORES_MASTERS_GROUP,
              icon: MASTERS[k].icon,
              permission: MASTERS[k].permission.view,
            })
          ),
          // Stores vendors are Vendors with "Stores vendor" on: the same master, filtered.
          link({
            to: masterPath("vendors"),
            search: { filter: "stores" },
            label: "Stores vendors",
            hint: "Vendors marked “Stores vendor”",
            group: STORES_MASTERS_GROUP,
            icon: MASTERS.vendors.icon,
            permission: MASTERS.vendors.permission.view,
          }),
          // The GOT masters (legacy QCS / QCM Masters menu), their own column (user, 2026-10-21:
          // out of Look-up into Masters). A GOT-only login sees Masters with this column alone.
          ...SPECIAL_SCREENS.filter((x) => x.permission.startsWith("got.")).map(
            (x) =>
              link({
                to: x.to,
                label: x.title,
                hint: SPECIAL_HINT[x.to],
                group: GOT_MASTERS_GROUP,
                icon: x.icon,
                permission: x.permission,
              })
          ),
        ].map(inMasterGroup),
      },
      link({
        to: "/admin/users",
        label: "Users",
        hint: "Logins, their plants and roles",
        icon: Users,
        permission: "users.manage",
      }),
      // Its own code, deliberately: shaping what a role grants and onboarding people are different
      // kinds of trust, and one can be given without the other.
      link({
        to: "/admin/roles",
        label: "Roles & permissions",
        hint: "What each role may open and do",
        icon: ShieldCheck,
        permission: "roles.manage",
      }),
      link({
        to: "/admin/integrations",
        label: "Integrations",
        hint: "Links to VNR Core and other systems",
        icon: Plug,
        permission: "integrations.manage",
      }),
      link({
        to: "/admin/audit-log",
        label: "Activity & Audit",
        hint: "Who worked when, and what they changed",
        icon: ScrollText,
        permission: "audit.activity.view",
        orPermission: "audit.view",
      }),
    ],
  },
  {
    // Reference pages, shown to logins holding help.view (user, 2026-09-30: Help is permission-based).
    // Last, and skipped by the "/" redirect, so it never becomes a login's landing page.
    title: "Help",
    items: [
      link({
        to: "/abbreviations",
        label: "Abbreviations",
        hint: "Short forms used across NexSeed",
        icon: BookA,
        permission: "help.view",
        reference: true,
      }),
      // Temporary, dev-phase client tracker (docs/designs/feedback-module.md); removed with it.
      link({
        to: "/feedback",
        label: "Feedback",
        icon: MessageSquareText,
        permission: "feedback.view",
        feature: "feedback",
        hint: "Requests, changes and bugs for the NexSeed team",
      }),
    ],
  },
];

/** Who is looking: their module codes, and the per-form grants that narrow them. */
export interface NavViewer {
  can: (p: Permission) => boolean;
  canViewForm: (formCode: string) => boolean;
  /** The Gate module's names for this login's roles; absent = the standard names. */
  gateLabels?: GateLabels | null | undefined;
  /** Optional modules switched on for this server; absent = none. */
  features?: NavFeatures | null | undefined;
}

const leafVisible = (l: NavLeaf, v: NavViewer) =>
  (!l.permission ||
    v.can(l.permission) ||
    (l.orPermission !== undefined && v.can(l.orPermission))) &&
  (!l.form || v.canViewForm(l.form)) &&
  (!l.anyForm || l.anyForm.some((f) => v.canViewForm(f))) &&
  (!l.anyPermission || l.anyPermission.some((c) => v.can(c))) &&
  (!l.allPermissions || l.allPermissions.every((c) => v.can(c))) &&
  (!l.feature || v.features?.[l.feature] === true) &&
  !l.hiddenWhen?.(v);

/**
 * QA floor and analyst logins (user, 2026-10-20): only the QA Supervisor, QA Manager and Lab Manager
 * have a QA dashboard, so a login holding qc.dashboard without one of their codes has no Dashboard
 * menu and lands on Home. Admin holds every code, so it is never QA floor.
 */
export function isQaFloor(v: Pick<NavViewer, "can">): boolean {
  return (
    v.can("qc.dashboard") &&
    !v.can("dashboard.qa_supervisor") &&
    !v.can("dashboard.qa_manager") &&
    !v.can("dashboard.lab_manager") &&
    !v.can("users.manage")
  );
}

/** The Masters group as a Stores-only login sees it: one list, the Stores vendors as its Vendors. */
function storesOnlyMasters(g: NavGroup): NavGroup {
  const vendors = masterPath("vendors");
  return {
    ...g,
    children: g.children
      .filter((c) => !(c.to === vendors && !c.search))
      .map((c) => {
        const { group: _group, ...rest } = c;
        if (c.to === vendors)
          return { ...rest, label: "Vendors", hint: "The store's vendors" };
        if (c.to === "/masters/locations")
          return { ...rest, hint: "The store's warehouses, bins and sub-bins" };
        return rest;
      }),
  };
}

/**
 * NAV as this login may see it: hidden leaves removed, and a group left with no children dropped.
 * The rail, the command palette and the "/" redirect all read this, so they cannot disagree.
 */
export function visibleNav(v: NavViewer): NavSection[] {
  const storesOnly = isStoresOnly(v.can);
  const renamed = v.gateLabels?.module;
  const named = (l: NavLeaf): NavLeaf =>
    l.gateModule && renamed ? { ...l, label: renamed } : l;
  return NAV.map((s) => ({
    ...s,
    title: s.gateModule && renamed ? renamed : s.title,
    items: s.items.flatMap((i): (NavLeaf | NavGroup)[] => {
      if (i.kind === "link") {
        if (!leafVisible(i, v)) return [];
        if (!i.subLinks) return [named(i)];
        const subs = i.subLinks.filter((c) => leafVisible(c, v));
        // No child it may open: no parent either. Two or more: the parent with just those.
        if (!subs.length) return [];
        if (subs.length > 1) return [{ ...named(i), subLinks: subs }];
        return subs.map(({ promotedLabel, ...c }) => ({
          ...c,
          label: promotedLabel ?? c.label,
        }));
      }
      if (i.permission && !v.can(i.permission)) return [];
      const children = i.children.filter((c) => leafVisible(c, v));
      if (!children.length) return [];
      const g = { ...i, children };
      return [storesOnly && i.match === "/masters" ? storesOnlyMasters(g) : g];
    }),
  }))
    .map((s) => {
      // "All Reports" only when there is more than one module's reports to choose between: with
      // one, the menu already lists them all (user, 2026-10-17).
      const hub = s.items.find((i) => i.kind === "link" && i.hub);
      if (!hub) return s;
      const groups = new Set(
        s.items.flatMap((i) =>
          i.kind === "link" && !i.hub ? [i.group ?? ""] : []
        )
      );
      return groups.size > 1
        ? s
        : { ...s, items: s.items.filter((i) => i !== hub) };
    })
    .filter((s) => s.items.some((i) => i.kind !== "link" || !i.hub));
}

/** Leaves the user may see, flattened (command palette "Go to", the "/" redirect). */
export function flatNav(v: NavViewer): (NavLeaf & { section: string })[] {
  return visibleNav(v).flatMap((s) =>
    s.items.flatMap((i) =>
      i.kind === "link"
        ? [i, ...(i.subLinks ?? [])].map((l) => ({ ...l, section: s.title }))
        : i.children.map((c) => ({ ...c, section: i.label }))
    )
  );
}

/**
 * Where "/" sends a signed-in login (user, 2026-10-20). A dashboard only on a dashboard's own code:
 * the plant Dashboard on dashboard.view (QA staff excepted, as the menu hides it from them), the QC
 * Dashboard on qc.dashboard -- never on a module or action code. Everyone else gets the Home welcome
 * and picks their work from the menu, rather than being dropped into whichever page the menu lists
 * first. Null when the login may open nothing but Help: the public landing page says so.
 */
export function landingTarget(v: NavViewer): string | null {
  // The QA Supervisor and Manager land on their sections; the QA floor and analysts on Home.
  if (v.can("dashboard.view") && !isQaFloor(v)) return "/dashboard";
  if (flatNav(v).some((l) => !l.reference)) return "/home";
  // A Packaging Kiosk login (user, 2026-11-02) has no menu page: it opens the kiosk screen.
  return v.can("packaging.kiosk") ? "/kiosk/packing" : null;
}

/** One entry in the top menu bar: a direct link, or a drop-down panel of links. */
export interface TopMenu {
  label: string;
  icon: LucideIcon;
  /** Set for a menu that is a single page: the bar links straight to it. */
  link?: NavLeaf | undefined;
  /** The drop-down panel's links, in NAV order. */
  items: NavLeaf[];
  /** Path prefixes that make this menu the current one. */
  match: string[];
}

/**
 * The top menu bar from the visible sections: a one-page section is a plain link, several pages
 * open a panel, and Admin's Masters group is its own menu. Help is left to the top strip, so it is
 * not a menu.
 */
export function topMenus(sections: NavSection[]): TopMenu[] {
  const menus: TopMenu[] = [];
  for (const s of sections) {
    if (s.title === "Help") continue;
    const links = s.items.filter((i): i is NavLeaf => i.kind === "link");
    for (const g of s.items) {
      if (g.kind === "group")
        menus.push({
          label: g.label,
          icon: g.icon,
          items: g.children,
          match: [g.match],
        });
    }
    if (links.length === 0) continue;
    const only =
      links.length === 1 && s.items.length === 1 ? links[0] : undefined;
    // The Gate's page carries the role's own name, so it is named by its page; other one-page sections
    // keep the heading (Dispatch, Inventory).
    // Overview is the Dashboard menu: one dashboard or a drop-down of them, always "Dashboard".
    const label =
      s.title === "Overview"
        ? "Dashboard"
        : only && s.gateModule
          ? only.label
          : s.title;
    menus.push({
      label,
      icon: s.icon ?? only?.icon ?? links[0]!.icon,
      link: only,
      items: links,
      match: links.filter((l) => !l.elsewhere).map((l) => l.to),
    });
  }
  return menus;
}

/**
 * The menu that owns this path: the one whose page path is the longest match, first winning a tie.
 * GOT's /quality/got/… and Look-up's /quality/standards sit under /quality, and
 * must light their own tab (user, 2026-10-16).
 */
export function currentMenu(
  menus: TopMenu[],
  pathname: string
): TopMenu | undefined {
  let best: TopMenu | undefined;
  let bestLen = -1;
  for (const m of menus)
    for (const to of m.match)
      if (
        (pathname === to || pathname.startsWith(`${to}/`)) &&
        to.length > bestLen
      ) {
        best = m;
        bestLen = to.length;
      }
  if (best) return best;
  // A module's own landing that is not a menu page (/arrivals lists every form's arrivals) still
  // belongs to the menu whose pages share its first segment, so the bar never goes unlit there.
  const seg = pathname.split("/")[1];
  if (!seg) return undefined;
  return menus.find((m) => m.match.some((to) => to.split("/")[1] === seg));
}

/** Work waiting under a menu: the sum of its pages' counts, or undefined when none of them has one. */
export function menuCount(
  m: TopMenu,
  byPath: Record<string, number> | undefined
): number | undefined {
  if (!byPath) return undefined;
  const paths = m.link ? [m.link.to] : m.items.map((i) => i.to);
  let total: number | undefined;
  for (const p of paths) {
    const n = byPath[p];
    if (n !== undefined) total = (total ?? 0) + n;
  }
  return total;
}

/** The Quality pages and the part of the counts each one shows. */
const QC_COUNT: Record<string, keyof NonNullable<NavCounts["qc"]>> = {
  "/quality/pending": "pending",
  "/quality/allocation": "unassigned",
  "/quality/moisture": "moisture",
  "/quality/germination": "germination",
  "/quality/pp": "pp",
  "/quality/blending": "blending",
  "/quality/releases/sr": "softReleaseFailed",
  "/quality/releases/ssr": "superSoftReleaseFailed",
  "/quality/guard-samples": "guardOverdue",
};

/** The Inventory pages that carry a number: approvals and reviews waiting, and balances off the ledger. */
const INVENTORY_COUNT: Record<string, "drift"> = {
  "/inventory/reconciliation": "drift",
};

/** GET /nav/counts laid onto the menu's pages, keyed by path; a page without a number is absent. */
export function countsByPath(
  c: NavCounts | undefined,
  sections: NavSection[]
): Record<string, number> | undefined {
  if (!c) return undefined;
  const out: Record<string, number> = {};
  for (const s of sections)
    for (const i of s.items)
      for (const l of i.kind === "link" ? [i] : i.children) {
        const qc = QC_COUNT[l.to];
        const inv = INVENTORY_COUNT[l.to];
        const n = inv
          ? c.inventory?.[inv]
          : l.gateModule
            ? c.gate
            : qc
              ? c.qc?.[qc]
              : l.form === DISPATCH_STOCK_TRANSFER_FORM
                ? c.dispatch
                : l.form
                  ? (c.arrivals?.[l.form] ?? c.processing?.[l.form])
                  : undefined;
        if (n !== undefined) out[l.to] = n;
      }
  return out;
}

/**
 * The list a form or record page belongs to: the menu page whose path this one sits under (longest
 * match), or undefined on a list itself and on pages outside the menu. The shell's Back button.
 */
export function backTarget(
  pathname: string,
  sections: NavSection[]
): NavLeaf | undefined {
  let best: NavLeaf | undefined;
  for (const s of sections)
    for (const i of s.items)
      for (const l of i.kind === "link" ? [i] : i.children) {
        if (pathname === l.to) return undefined;
        if (
          pathname.startsWith(`${l.to}/`) &&
          (!best || l.to.length > best.to.length)
        )
          best = l;
      }
  return best;
}
